const StorageTypes = require('../jsdoc-types'); // eslint-disable-line no-unused-vars
const { isNumber, isObject } = require('../../type');
const {
  executeWithPurgeAndRetryIfQuotaExceeded,
} = require('../errors/QuotaExceededError');
const purgeStorage = require('../utils/purgeStorage');

/**
 * Enum for TTL purge policies.
 *
 * Specifies when expired entries should be automatically removed from the
 * underlying storage.
 *
 * @readonly
 * @enum {string}
 */
const PURGE_POLICY = {
  RECONSTRUCT: 'reconstruct',
  WRITE: 'write',
  QUOTA: 'quota',
};

/**
 * Determines whether a stored value has expired.
 *
 * A value is considered expired only if it contains an `expiresAt` timestamp
 * and the current time is greater than that value.
 *
 * This function is designed to be used as a predicate for `purgeStorage`.
 *
 * @param {Object} entry
 * An object describing a storage entry.
 *
 * @param {any} entry.value
 * The stored value associated with the key.
 *
 * @returns {boolean}
 * `true` if the value has expired; otherwise `false`.
 */
const isItemExpired = ({ value }) =>
  value?.expiresAt ? Date.now() > value.expiresAt : false;

/**
 * Determines whether a stored value follows a legacy (non-TTL) format.
 *
 * Legacy values are:
 * - not objects, or
 * - objects without a `value` property, or
 * - objects without an `expiresAt` property
 *
 * Such values are treated as non-expiring and are preserved as-is.
 *
 * @param {any} value
 * The stored value to evaluate.
 *
 * @returns {boolean}
 * `true` if the value does not conform to the TTL wrapper format;
 * otherwise `false`.
 */
const isLegacyItem = (value) =>
  !isObject(value) ||
  value.value === undefined ||
  value.expiresAt === undefined;

/**
 * Creates a TTL storage decorator.
 *
 * This factory configures TTL behavior and returns a decorator function.
 * The returned decorator can later be applied to any object implementing
 * the Web Storage API (`localStorage`, `sessionStorage`, or compatible mocks).
 *
 * Legacy values (non-TTL) are preserved and never expire.
 *
 * @param {number} ttlMilliseconds
 * Time-to-live in milliseconds. Only positive numbers enable TTL behavior.
 *
 * @param {Object} [options]
 * Additional configuration options.
 *
 * @param {PURGE_POLICY[]} [options.purgePolicies=[PURGE_POLICY.QUOTA, PURGE_POLICY.RECONSTRUCT]]
 * Determines when expired values are purged:
 * - `RECONSTRUCT`: purge on decorator application
 * - `WRITE`: purge after each `setItem`
 * - `QUOTA`: purge only on quota pressure or explicit trigger
 *
 * @returns {StorageTypes.StorageDecorator}
 * A decorator function that applies TTL behavior to a storage instance.
 */
function create(
  ttlMilliseconds,
  { purgePolicies = [PURGE_POLICY.QUOTA, PURGE_POLICY.RECONSTRUCT] } = {}
) {
  const ttl =
    isNumber(ttlMilliseconds) && ttlMilliseconds > 0 ? ttlMilliseconds : 0;

  return function decorate(baseStorage) {
    if (ttl === 0) {
      return baseStorage;
    }

    const purge = () => {
      // eslint-disable-next-line no-param-reassign
      baseStorage = purgeStorage(isItemExpired, baseStorage);
    };

    if (purgePolicies.includes(PURGE_POLICY.RECONSTRUCT)) {
      purge();
    }

    return {
      getItem(key) {
        const raw = baseStorage.getItem(key);
        if (raw == null) return null;

        if (isLegacyItem(raw)) {
          return raw;
        }

        const { value, expiresAt } = raw;

        if (!expiresAt) {
          return value;
        }

        if (Date.now() > expiresAt) {
          baseStorage.removeItem(key);
          return null;
        }

        return value;
      },

      setItem(key, value) {
        const expiresAt = Date.now() + ttl;
        const wrapped = { value, expiresAt };

        if (purgePolicies.includes(PURGE_POLICY.QUOTA)) {
          executeWithPurgeAndRetryIfQuotaExceeded({
            operation: () => baseStorage.setItem(key, wrapped),
            purge,
            context: {
              key,
              attemptedValue: wrapped,
              purgePolicies,
            },
          });
        } else {
          baseStorage.setItem(key, wrapped);
        }

        if (purgePolicies.includes(PURGE_POLICY.WRITE)) {
          purge();
        }
      },

      removeItem(key) {
        baseStorage.removeItem(key);
      },

      clear() {
        baseStorage.clear();
      },

      get length() {
        return baseStorage.length;
      },

      key(index) {
        return baseStorage.key(index);
      },
    };
  };
}

module.exports = {
  isItemExpired,
  create,
  PURGE_POLICY,
};
